import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Badge, Select } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { isSuperAdmin, useMyPermissions } from '../auth/useMyPermissions';
import { useSupplierOrganizations } from './hooks';
import type { ReactNode } from 'react';

/**
 * Mimic Mode: a super admin looking at the supplier portal as a chosen
 * factory, from their own login.
 *
 * Two rules shape everything here.
 *
 * The org is CARRIED, never inferred. is_active_supplier_of() now returns
 * true for a super admin against every organization, which is what lets the
 * portal load real data — but it also means any query that trusted RLS to
 * narrow to a single row now gets all of them. So the chosen organization is
 * passed explicitly to every query that needs one; nothing reads "whichever
 * row came back first".
 *
 * And it is READ-ONLY until somebody says otherwise. A write made while
 * mimicking lands with created_by set to the super admin while being
 * presented as a supplier action, and the review flow reads created_by to
 * decide who did what. Turning that on is a deliberate switch, not a default.
 *
 * Nothing here touches the super admin's profile. Their account_type, role
 * and organization are exactly what they were before and after.
 */

interface MimicState {
  /** True when a super admin is viewing the portal as somebody else. */
  isMimicking: boolean;
  /** The organization the portal should query. '' when not resolved yet. */
  organizationId: string;
  /** False unless the super admin has explicitly enabled test writing. */
  canWrite: boolean;
}

const MimicContext = createContext<MimicState>({
  isMimicking: false,
  organizationId: '',
  canWrite: false,
});

export function useMimic(): MimicState {
  return useContext(MimicContext);
}

/**
 * The organization the current viewer's portal queries should use.
 *
 * A real supplier gets their own, from their profile — unchanged behaviour.
 * A mimicking super admin gets the one they picked. Every portal query that
 * needs an organization id should come through here rather than reaching for
 * the profile, because a super admin's profile has no organization at all.
 */
export function useEffectiveSupplierOrg(): string {
  const { access } = useAuth();
  const mimic = useMimic();
  if (mimic.isMimicking) {
    return mimic.organizationId;
  }
  return access.status === 'approved' ? (access.profile.supplier_organization_id ?? '') : '';
}

export function MimicProvider({ children }: { children: ReactNode }) {
  const { access } = useAuth();
  const permissions = useMyPermissions();
  const organizations = useSupplierOrganizations();
  const [chosen, setChosen] = useState('');
  const [canWrite, setCanWrite] = useState(false);

  const viewer = access.status === 'approved' ? access.profile : null;
  // A real supplier is never mimicking, however many permissions they hold.
  const mimicking = viewer?.account_type === 'tap' && isSuperAdmin(permissions.data);

  const rows = organizations.data ?? [];
  // Default to the first organization only once the list has loaded, so the
  // portal never queries with '' and renders an empty state that looks like
  // a permissions failure.
  const organizationId = chosen !== '' ? chosen : (rows[0]?.id ?? '');

  const value = useMemo(
    () => ({ isMimicking: mimicking, organizationId, canWrite: mimicking ? canWrite : true }),
    [mimicking, organizationId, canWrite],
  );

  const onPick = useCallback((next: string) => setChosen(next), []);
  const onToggleWrite = useCallback(() => setCanWrite((current) => !current), []);

  return (
    <MimicContext.Provider value={value}>
      {mimicking ? (
        <MimicBanner
          organizationId={organizationId}
          organizations={rows}
          canWrite={canWrite}
          onPick={onPick}
          onToggleWrite={onToggleWrite}
        />
      ) : null}
      {children}
    </MimicContext.Provider>
  );
}

function MimicBanner({
  organizationId,
  organizations,
  canWrite,
  onPick,
  onToggleWrite,
}: {
  organizationId: string;
  organizations: { id: string; name: string }[];
  canWrite: boolean;
  onPick: (organizationId: string) => void;
  onToggleWrite: () => void;
}) {
  return (
    <aside className="mimic-banner" aria-label="Mimic Mode">
      <span className="mimic-banner-title">
        <span aria-hidden="true">🛡️</span> Mimic Mode
      </span>
      <Select
        label="Viewing as"
        value={organizationId}
        options={organizations.map((organization) => ({
          value: organization.id,
          label: organization.name,
        }))}
        onChange={(event) => onPick(event.target.value)}
      />
      <button
        type="button"
        className="mimic-banner-toggle"
        aria-pressed={canWrite}
        onClick={onToggleWrite}
      >
        <span aria-hidden="true">⚡</span> Enable test writing: {canWrite ? 'ON' : 'OFF'}
      </button>
      <Badge tone={canWrite ? 'warning' : 'neutral'}>
        {canWrite ? 'Writes land as you' : 'Read-only'}
      </Badge>
    </aside>
  );
}
